import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, Star } from "lucide-react";
import { toastSuccess, toastError, pickErrorMsg } from "../lib/feedback";

const CAMPOS = [["resumo", "Resumo"], ["origem", "Origem"], ["composicao", "Composição"], ["acabamentos", "Acabamentos"], ["indicacoes", "Indicações de uso"], ["cuidados", "Cuidados"]];
const ta = { width: "100%", background: "var(--bg-1)", border: "1px solid var(--line)", color: "var(--ink)", padding: 8, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" };
const StatusTag = ({ ok }) => <span style={{ fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase", padding: "2px 7px", border: `1px solid ${ok ? "#5fa86c" : "var(--line)"}`, color: ok ? "#5fa86c" : "var(--ink-3)" }}>{ok ? "publicada no SB" : "rascunho"}</span>;

function FichaItem({ m, ficha, api, onReload }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toastSuccess(ok); setEdit(null); await onReload(); } catch (e) { toastError("Falhou", { description: pickErrorMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <div style={{ borderTop: "1px solid var(--line)", padding: "14px 0" }} data-testid={`ficha-admin-${m.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ color: "var(--ink)" }}>{m.nome}</strong>
        {ficha && <StatusTag ok={ficha.status === "aprovada"} />}
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => run(() => api("post", `/materiais/${m.id}/ficha/gerar`), "Ficha gerada — revise antes de publicar")} data-testid={`ficha-gerar-${m.id}`}>
          <Sparkles size={11} /> {busy ? "Pesquisando…" : ficha ? "Pesquisar de novo" : "Gerar com IA"}
        </button>
        {ficha && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setEdit(edit ? null : { ...ficha.conteudo })} data-testid={`ficha-editar-${m.id}`}>{edit ? "Fechar" : "Revisar"}</button>}
        {ficha && <button className="btn btn-sm" disabled={busy} onClick={() => run(() => api("post", `/materiais/${m.id}/ficha/aprovar`, { aprovada: ficha.status !== "aprovada" }))} data-testid={`ficha-aprovar-${m.id}`}>{ficha.status === "aprovada" ? "Despublicar" : "Aprovar e publicar"}</button>}
      </div>
      {edit && (
        <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
          {CAMPOS.map(([k, l]) => (
            <label key={k} style={{ fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase", color: "var(--ink-3)" }}>{l}
              <textarea rows={2} value={edit[k] || ""} onChange={e => setEdit({ ...edit, [k]: e.target.value })} style={ta} data-testid={`ficha-campo-${k}-${m.id}`} />
            </label>
          ))}
          {ficha.fontes?.length > 0 && <div style={{ fontSize: 11, color: "var(--ink-3)", overflowWrap: "anywhere" }}>Fontes: {ficha.fontes.map(u => <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color: "var(--gold)", marginRight: 8 }}>{u}</a>)}</div>}
          <button className="btn btn-primary btn-sm" style={{ justifySelf: "end" }} disabled={busy} onClick={() => run(() => api("put", `/materiais/${m.id}/ficha`, { conteudo: edit, fontes: ficha.fontes || [] }), "Ficha salva")} data-testid={`ficha-salvar-${m.id}`}>Salvar revisão</button>
        </div>
      )}
    </div>
  );
}

function FichasAdmin({ obra, api }) {
  const [itens, setItens] = useState(null);
  const load = useCallback(() => api("get", `/obras/${obra.id}/fichas-tecnicas`).then(setItens).catch(() => setItens([])), [api, obra.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="panel" style={{ marginTop: 24 }} data-testid="fichas-admin">
      <div className="panel-head"><div><div className="panel-eyebrow">Pesquisa na internet · revisão do admin</div><h3 className="panel-title">Ficha técnica <em>dos materiais</em></h3></div></div>
      {itens === null ? null : itens.length === 0 ? <p style={{ color: "var(--ink-3)", fontSize: 13 }}>Vincule materiais à obra (aba Materiais) para gerar as fichas.</p>
        : itens.map(({ material, ficha }) => <FichaItem key={material.id} m={material} ficha={ficha} api={api} onReload={load} />)}
    </div>
  );
}

function ValorizacaoAdmin({ obra, api }) {
  const [v, setV] = useState(null);
  const [tipo, setTipo] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", `/obras/${obra.id}/valorizacao`).then(d => { setV(d); setTipo(d.tipo_reforma || ""); }).catch(() => { }), [api, obra.id]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toastSuccess(ok); await load(); } catch (e) { toastError("Falhou", { description: pickErrorMsg(e) }); } finally { setBusy(false); }
  };
  if (!v) return null;
  const tem = !!v.texto;
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  return (
    <div className="panel" style={{ marginTop: 24 }} data-testid="valorizacao-admin">
      <div className="panel-head"><div><div className="panel-eyebrow">Mercado imobiliário · {v.cidade || "cidade da obra"}</div><h3 className="panel-title">Valorização <em>estimada</em></h3></div>{tem && <StatusTag ok={v.status === "aprovada"} />}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input value={tipo} onChange={e => setTipo(e.target.value)} placeholder="Tipo de reforma (ex: banheiros de alto padrão em mármore)" style={{ ...ta, flex: "1 1 280px" }} data-testid="valor-tipo" />
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => run(() => api("post", `/obras/${obra.id}/valorizacao/gerar`, { tipo_reforma: tipo }), "Pesquisa concluída — revise antes de publicar")} data-testid="valor-gerar"><Sparkles size={11} /> {busy ? "Pesquisando…" : "Pesquisar com IA"}</button>
      </div>
      {tem && (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <textarea rows={3} value={v.texto || ""} onChange={set("texto")} style={ta} data-testid="valor-texto" />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input type="number" step="0.1" value={v.faixa_min ?? ""} onChange={set("faixa_min")} placeholder="mín %" style={{ ...ta, width: 90 }} data-testid="valor-min" />
            <input type="number" step="0.1" value={v.faixa_max ?? ""} onChange={set("faixa_max")} placeholder="máx %" style={{ ...ta, width: 90 }} data-testid="valor-max" />
            <input value={v.fonte_nome || ""} onChange={set("fonte_nome")} placeholder="Fonte" style={{ ...ta, width: 160 }} data-testid="valor-fonte-nome" />
            <input value={v.fonte_url || ""} onChange={set("fonte_url")} placeholder="Link da fonte" style={{ ...ta, flex: "1 1 220px" }} data-testid="valor-fonte-url" />
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>Consulta em {v.data_consulta}{v.fontes?.length ? ` · ${v.fontes.length} fonte(s) encontradas` : ""}</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => run(() => api("put", `/obras/${obra.id}/valorizacao`, {
              tipo_reforma: tipo, texto: v.texto, fonte_nome: v.fonte_nome, fonte_url: v.fonte_url,
              faixa_min: v.faixa_min === "" ? null : Number(v.faixa_min), faixa_max: v.faixa_max === "" ? null : Number(v.faixa_max),
            }), "Revisão salva")} data-testid="valor-salvar">Salvar revisão</button>
            <button className="btn btn-sm" disabled={busy} onClick={() => run(() => api("post", `/obras/${obra.id}/valorizacao/aprovar`, { aprovada: v.status !== "aprovada" }))} data-testid="valor-aprovar">{v.status === "aprovada" ? "Despublicar" : "Aprovar e publicar"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AvaliacoesAdmin({ obra, api }) {
  const [lista, setLista] = useState([]);
  const load = useCallback(() => api("get", `/avaliacoes?obra_id=${obra.id}`).then(setLista).catch(() => { }), [api, obra.id]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (a) => { await api("patch", `/avaliacoes/${a.id}`, { aprovada: !a.aprovado }); load(); };
  return (
    <div className="panel" style={{ marginTop: 24 }} data-testid="avaliacoes-admin">
      <div className="panel-head"><div><div className="panel-eyebrow">Aparece no SB quando a obra está Concluída</div><h3 className="panel-title">Avaliação <em>do cliente</em></h3></div></div>
      {lista.length === 0 ? <p style={{ color: "var(--ink-3)", fontSize: 13 }}>{obra.status === "concluida" ? "Aguardando a avaliação do cliente." : "Marque a obra como Concluída para abrir a avaliação no Signature Book."}</p> : lista.map(a => (
        <div key={a.id} data-testid={`avaliacao-${a.id}`} style={{ borderTop: "1px solid var(--line)", padding: "12px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span>{[1, 2, 3, 4, 5].map(n => <Star key={n} size={14} color="#b8935a" fill={n <= a.nota ? "#b8935a" : "transparent"} />)}</span>
            <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{new Date(a.at).toLocaleDateString("pt-BR")}</span>
            <div style={{ flex: 1 }} />
            {a.depoimento && <button className="btn btn-sm" onClick={() => toggle(a)} data-testid={`avaliacao-aprovar-${a.id}`}>{a.aprovado ? "✓ Aprovado como prova social" : "Aprovar depoimento"}</button>}
          </div>
          {a.depoimento && <p style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--ink-2)", margin: "8px 0 0" }}>"{a.depoimento}"</p>}
        </div>
      ))}
    </div>
  );
}

export function SBExtrasAdmin({ obra, api }) {
  return (<><FichasAdmin obra={obra} api={api} /><ValorizacaoAdmin obra={obra} api={api} /><AvaliacoesAdmin obra={obra} api={api} /></>);
}
